import type { IField, OtherInfoField } from '@moko/utils';
import { getFieldPanelContext } from '@moko/utils';
import { isFieldReadOnly } from './fieldReadOnly';
import { useOptionalOtherInfoContext } from './OtherInfoContext';

/**
 * Resolves a field's value from otherInfoObj, handling root-level and repeating panel contexts.
 * Pure function -- no hooks, no side effects. Reusable anywhere a field value needs to be read.
 */
export const resolveFieldValue = (
  fieldName: string,
  otherInfoObj: Record<string, any>,
  metadataFields: OtherInfoField[],
  getRepeatingPanelIndex: (panelName: string) => number,
  currentElementInEntityRepeatingPanel: number
): any => {
  const { fieldParentName, manualRepeating, entityRepeating } =
    getFieldPanelContext(fieldName, metadataFields);

  if (fieldParentName && manualRepeating) {
    const index = getRepeatingPanelIndex(fieldParentName);
    return otherInfoObj[fieldParentName]?.[index]?.[fieldName];
  }
  if (fieldParentName && entityRepeating) {
    const entityIndex = otherInfoObj[fieldParentName]?.findIndex(
      (entity: any) => entity.id === currentElementInEntityRepeatingPanel
    );
    if (entityIndex >= 0) {
      return otherInfoObj[fieldParentName]?.[entityIndex]?.[fieldName];
    }
    return undefined;
  }
  return otherInfoObj[fieldName];
};

export const useFieldState = (field: IField) => {
  const ctx = useOptionalOtherInfoContext();
  if (!ctx) {
    return {
      fieldValue: undefined,
      isFieldDisabled: isFieldReadOnly(field),
      isSaved: false,
      validationMessage: undefined,
      metadata: undefined,
      allOptionsObj: {},
    };
  }

  const {
    params,
    otherInfoObj,
    allOptionsObj,
    getRepeatingPanelIndex,
    currentElementInEntityRepeatingPanel,
    hasActiveRepeatingInstance,
    hasActiveEntityRepeatingInstance,
  } = ctx;

  const { metadata, validationErrors } = params;
  const isSaved = params.isSaved.current;

  const fieldName = field.name;
  const { fieldParentName, manualRepeating, entityRepeating } =
    getFieldPanelContext(fieldName, metadata.fields);

  const hasRepeatingItem =
    fieldParentName && manualRepeating
      ? hasActiveRepeatingInstance(fieldParentName)
      : fieldParentName && entityRepeating
      ? hasActiveEntityRepeatingInstance(fieldParentName)
      : true;
  const repeatingBlocked =
    (manualRepeating || entityRepeating) && !hasRepeatingItem;
  const isFieldDisabled = Boolean(
    params.readOnly || isSaved || isFieldReadOnly(field) || repeatingBlocked
  );

  const fieldValue = resolveFieldValue(
    fieldName,
    otherInfoObj,
    metadata.fields,
    getRepeatingPanelIndex,
    currentElementInEntityRepeatingPanel
  );

  const validationMessage = validationErrors?.find(
    (e) => e.fieldName === field.name
  )?.message;

  return {
    fieldValue,
    isFieldDisabled,
    isSaved,
    validationMessage,
    metadata,
    allOptionsObj,
  };
};
